import { ChangeDetectorRef, Directive, OnDestroy, OnInit } from '@angular/core';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import sortBy from 'lodash-es/sortBy';
import { Subscription } from 'rxjs/internal/Subscription';
import { Question, QuestionType } from './question';

export interface ICheckoutDeliveryQuestionsData {
    [key: number]: 1 | 0 | string | ICheckoutDeliveryTextareaData;
}

export interface ICheckoutDeliveryTextareaData {
    value: 1 | 0;
    message: string;
}

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface IQuoteDataCustom {
            questions?: ICheckoutDeliveryQuestionsData;
        }
    }
}

@Directive()
export class CheckoutDeliveryQuestionsComponentDef implements OnInit, OnDestroy {
    protected subscriptions = new Subscription();

    /**
     * Questions models
     * @type {Question[] | undefined}
     */
    questions?: Question[];

    /**
     * Status model
     * @type {Status}
     */
    status = new Status();

    save = this.timeoutService.debounce(() => this._save(), 1000);

    constructor(
        protected checkoutFormComponent: CheckoutFormComponentDef,
        protected checkoutStepComponent: CheckoutStepComponentDef,
        protected app: App,
        protected changeDetectorRef: ChangeDetectorRef,
        private timeoutService: TimeoutService
    ) {}

    ngOnInit() {
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.subscriptions.add(this.checkoutStepComponent.submittedSubject.subscribe(() => this.detectChanges()));
        this.app.request.send(
            this.status,
            () => this.app.resources.question.find({ verbosity: 1, filter: {} }),
            () => {
                const questions = sortBy(this.app.resources.question.getAllExisting(), question => question.data.sort_order);
                questions.forEach(question => {
                    if (question.questionType === QuestionType.TextArea) {
                        if (!this.data[question.id]) {
                            this.data[question.id] = { value: undefined, message: '' };
                        }
                    }
                });
                this.questions = questions;
                this.detectChanges();
            }
        );
    }

    /**
     * on destroy callback
     * we remove answers from custom data on component destroy
     */
    ngOnDestroy() {
        if (this.checkoutFormComponent.data.custom?.questions) {
            delete this.checkoutFormComponent.data.custom.questions;
            this._save();
        }
        this.subscriptions.unsubscribe();
    }

    get data(): ICheckoutDeliveryQuestionsData {
        if (!this.checkoutFormComponent.data.custom) {
            this.checkoutFormComponent.data.custom = {};
        }

        if (!this.checkoutFormComponent.data.custom.questions || Array.isArray(this.checkoutFormComponent.data.custom.questions)) {
            this.checkoutFormComponent.data.custom.questions = {};
        }

        return this.checkoutFormComponent.data.custom.questions;
    }

    /**
     * Is shipping method selected getter
     * @returns {boolean}
     */
    get isMethodSelected(): boolean {
        return !!this.checkoutFormComponent.shippingMethodCode;
    }

    /**
     * Sends save checkout custom data request
     */
    protected _save(): void {
        this.checkoutFormComponent._saveCustomData();
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    trackById(index: number, question: Question): number {
        return question.id;
    }
}
